import { Middleware } from 'redux';
import toolIndex from "toolConstants";
import ReducersIndex from 'reduxModules/ducks';
import { capitalize, findLast, isEmpty, keys, toPairs, pick, every, get, find } from "lodash";
import { getAreaList, getAvailableArea } from "reduxModules/selectors/containerSelectors";

interface Store {
  getState: () => any;
  dispatch: (action: any) => void;
}

const connectorMiddleware: Middleware = ({ dispatch, getState }: Store) => (next) => (action) => {
  let store = getState();

  // Set Active Tab to latest added area or container
  const setTab = () => {
    const tab: any = {};
    if (keys(tab).length === 0) {
      ({ id: tab.id, name: tab.name } = findLast(store.container.areas) ?? { id: null, name: null });
    };
    dispatch(ReducersIndex.uiReducers.updateTab(tab));
    dispatch(ReducersIndex.uiReducers.setActiveTab(tab.id));
  };

  // Pre-action items: These needs to run before the reducers are executed
  switch (true) {
    case action.type === ReducersIndex.uiReducers.closeModal.type && action.payload === 'tutorial':
      //if (store.ui.modalReopen) dispatch(ReducersIndex.uiReducers.openModal(store.ui.modalReopen));
      dispatch(ReducersIndex.uiReducers.setModalReopen(null));
      break;
    case action.type === ReducersIndex.uiReducers.openModal.type && action.payload === 'tutorial':
      const firstTimer = store.ui.isFirstTimeUser
      const currentModal = store.ui.modals.find((modal: any) => modal.modalName !== 'tutorial' && modal.modalOpen)?.modalName;
      const target = toolIndex[store.ui.tool]?.modals['launch']?.target;
      // For Conference Room Designer, no target modal since it uses walkthrough guide
      const modalReopen = firstTimer ? (target || null) : currentModal;
      dispatch(ReducersIndex.uiReducers.setModalReopen(modalReopen));
      if (currentModal) dispatch(ReducersIndex.uiReducers.closeModal(currentModal));
      break;

    case action.type === ReducersIndex.uiReducers.setAllowAddArea.type:
      if (action.payload === undefined) {
        const areas = getAreaList(store);
        action.payload = areas.at(-1) === store.container.activeTab && areas.length < toolIndex[store.ui.tool].constants.tool.maximumAreas && !isEmpty(getAvailableArea(store.container));
      };
      break;

    case action.type === ReducersIndex.container.setLoadingDocks.type:
      const id = action.payload;
      const hasArea = id in store.container.areas;
      const currentState = store.container.loadingDocks[id]; // defines if we're adding or deleting
      const name = `${capitalize(id).replace('_', ' ')}`;
      dispatch(ReducersIndex.container.setAreaToUpdate({ id: id, name: name, areaType: 'dock' }));

      if (store.ui.isModalOpen) break
      if (currentState && hasArea) {
        dispatch(ReducersIndex.uiReducers.openModal('removeDockArea'));
      } else if (!currentState && !hasArea) {
        dispatch(ReducersIndex.uiReducers.openModal('addDockArea'));
      } else {
        dispatch(ReducersIndex.container.setAreaToUpdate(null));
      };

      break;

    case action.type === ReducersIndex.container.deleteArea.type:
      // Remove the tab after deleting and navigate to warehouse 
      const areaId = store.container.areaToUpdate.id;
      dispatch(ReducersIndex.uiReducers.deleteTab(areaId));
      const newTabArray = store.ui.tabs.filter((tab: any) => tab.id !== areaId);
      if (newTabArray.length > 0) {
        dispatch(ReducersIndex.uiReducers.setActiveTab(newTabArray[0].id));
      } else {
        dispatch(ReducersIndex.uiReducers.setExitToRoom(false));
        dispatch(ReducersIndex.container.addArea(undefined));
        // Only open 'area' modal for non-Conference Room Designer tools (CRD uses walkthrough guide)
        if (store.ui.tool !== 'conferenceroomDesigner') {
          dispatch(ReducersIndex.uiReducers.openModal('area'));
        }
      }
      break;
      
    case action.type === ReducersIndex.container.setSpeakerModel.type:
      const newSpeaker = action.payload;
      const currentSpeaker = store.container.areas[store.container.activeTab].speakerModel;
      if (currentSpeaker && newSpeaker && newSpeaker !== currentSpeaker) {
        dispatch(ReducersIndex.uiReducers.openModal('changeSpeaker'));
        const customParameters = toolIndex[store.ui.tool].constants.customSettings.speaker.model;
        dispatch(ReducersIndex.container.setCustomized(customParameters));
      };
      break;

    default:
      break;
  }

  // Execute the reducer and get the state before proceding 
  next(action);

  store = getState();

  const tab: any = {};
  const toolConstants = toolIndex[store.ui.tool]?.constants.tool;
  // Post-action items: Theses actions happen after the reducer is executed as they require current state
  const areas = keys(store.container.areas);
  const currentTable = store.container.areas[store.container.activeTab]?.table;


  switch (true) {

    case action.type === ReducersIndex.uiReducers.setTool.type:
      if (toolConstants.containerName) {
        tab.id = `_${toolConstants.containerName.toLowerCase()}`;
        tab.name = toolConstants.containerName;
      }
      dispatch(ReducersIndex.uiReducers.setAllowAddArea(areas.length < toolConstants.maximumAreas));
      setTab();
      break;

    // setPlatform mutates productFamily in two directions (cascade Devio/Tesira
    // on Android entry, restore the pre-Android snapshot on exit) — both via
    // Immer, which the family Selector misses. Fire a follow-up setProductFamily
    // for ANY setPlatform action so the Selector re-renders both directions.
    // No-op when family didn't actually change.
    case action.type === ReducersIndex.container.setPlatform.type: {
      const settledFamily = store.container.areas[store.container.activeTab]?.productFamily;
      if (settledFamily) dispatch(ReducersIndex.container.setProductFamily(settledFamily));
      break;
    }

    case action.type === ReducersIndex.container.addArea.type:
      dispatch(ReducersIndex.uiReducers.setAllowAddArea(areas.length < toolConstants.maximumAreas));
      setTab();
      break;

    case action.type === ReducersIndex.container.duplicateArea.type:
      setTab();
      break;

    case action.type === ReducersIndex.uiReducers.modalForward.type:
      if (store.ui.modalForward) {
        // todo: this is warehouse Specific so it shoukld live somewhere else 
        if (areas.length < 1) {
          const docks = store.container.loadingDocks;
          for (const [dock, value] of toPairs(docks)) {
            if (value) {
              const name = `${capitalize(dock).replace('_', ' ')}`;
              dispatch(ReducersIndex.container.setAreaToUpdate({ id: dock, name: name, areaType: 'dock' }))
              dispatch(ReducersIndex.container.addArea({ areaToAdd: 'areaToUpdate' }));
            };
          };
          dispatch(ReducersIndex.container.addArea(undefined));
        } else {
          const key = areas[areas.findIndex(k => k === store.container.activeTab) + 1];
          dispatch(ReducersIndex.uiReducers.setActiveTab(key));
        };
      } else {
        dispatch(ReducersIndex.uiReducers.setAllowAddArea(false));
        const index = areas.indexOf(store.container.activeTab);
        dispatch(ReducersIndex.uiReducers.setActiveTab(index <= 0 ? store.ui.tabs[0].id : areas[index - 1]))
      };
      break;

    case action.type === ReducersIndex.container.setDimensions.type:
    case action.type === ReducersIndex.container.setLoadingDocks.type:
    case action.type === ReducersIndex.container.deleteArea.type:
      if (toolConstants.container) {
        dispatch(ReducersIndex.uiReducers.setAllowAddArea(!isEmpty(getAvailableArea(store.container))));
      };
      if (keys(store.container.areas).length < 1 && store.ui.exitToRoom) {
        dispatch(ReducersIndex.uiReducers.setExitToRoom(false));

        dispatch(ReducersIndex.container.addArea(undefined));
        // Only open 'area' modal for non-Conference Room Designer tools (CRD uses walkthrough guide)
        if (store.ui.tool !== 'conferenceroomDesigner') {
          dispatch(ReducersIndex.uiReducers.openModal('area'));
        }

      }
      if (currentTable?.shape) {
        dispatch(ReducersIndex.container.setTable(pick(currentTable, ['shape']) as any));
      };

      break;

    case action.type === ReducersIndex.container.setTable.type:
      // check if table fits in the room
      if (currentTable?.shape) {
        const isModalOpen = store.ui.isModalOpen;
        const currentModal = isModalOpen ? get(find(store.ui.modals, { modalOpen: true }), 'modalName') : null;
        const currentView = store.ui.currentView;
        const { minimum: minimumTableSize = { length: 0, width: 0 } } = toolIndex[store.ui.tool].constants.tables[currentTable.shape] ?? {};
        const hasValidSize = every(keys(currentTable.size), prop => currentTable.size[prop] >= minimumTableSize[prop]);
        // special case when navigatig backwards in modals and resizing room when table has already been defined
        if (!hasValidSize) {
          if (isModalOpen && currentModal === 'area') {
            dispatch(ReducersIndex.container.setTable({ shape: null } as any));
            return;
          };

          const error = {
            code: '1050',
            msg: `Room size is too small to fit the selected table shape.${currentView === 'floorplan' ? '\nTable will be removed.' : ''}`,
            parameter: 'tableSelect'
          };
          if (!isModalOpen) dispatch(ReducersIndex.container.setAreaToUpdate({ area: store.container.activeTab, update: { parameter: 'table', value: { shape: null } } }));
          dispatch(ReducersIndex.uiReducers.setError(error));
        } else {
          dispatch(ReducersIndex.uiReducers.setError({ code: '1050' }));
        };
      } else {
        dispatch(ReducersIndex.uiReducers.setError({ code: '1050' }));
      };
      break;

    default:
      // next(action)
      break;

  }


};



export default connectorMiddleware;
// This is listening to everything !